import { useEffect, useRef, useCallback } from "react";

/**
 * useDjMixerEngine — Web Audio graph for the DJ studio.
 *
 *   audio A  ──► lowShelf ──► peaking ──► highShelf ──► chanGain A ──┐
 *                                                                    ├──► masterGain ──► ctx.destination  (speakers)
 *   audio B  ──► lowShelf ──► peaking ──► highShelf ──► chanGain B ──┤                       │
 *                                                                    │                       └──► broadcastDest (MediaStream) — used by the upcoming
 *                                                                    │                              live broadcast pipeline (WebRTC / Icecast / HLS)
 *   mic      ──► micGain ──► (fxSend → preFx) or (fxBypass → master) │
 *
 * Mic accepts either Talk-mode constraints (echo-cancel + noise suppress, mono)
 * or Music-mode constraints (no DSP, stereo) so a DJ can plug a phone or audio
 * interface into the mic jack and use it as a clean line-in source.
 *
 * Each BiquadFilter gain is driven in dB by the knob value (0..1):
 *     knob 0.5  → 0dB
 *     knob 0    → -40dB (kill)
 *     knob 1    → +12dB
 *
 * Channel gain is driven from the parent's crossfader + music/master vols.
 * The graph creates ONE MediaElementAudioSourceNode per <audio> element.
 * Because you can't re-create a source on the same element, the engine
 * reuses cached graph nodes when the audio element is remounted only if
 * the element reference actually changed.
 */

const knobToDb = (v) => {
  if (v <= 0.5) {
    // Lower half — exponential kill down to -40dB
    const n = v / 0.5; // 0..1
    return -40 * (1 - n);
  }
  // Upper half — linear boost up to +12dB
  const n = (v - 0.5) * 2; // 0..1
  return n * 12;
};

export default function useDjMixerEngine({
  audioARef, audioBRef, trackAId, trackBId,
  eqA = { low: 0.5, mid: 0.5, hi: 0.5, gain: 0.5 },
  eqB = { low: 0.5, mid: 0.5, hi: 0.5, gain: 0.5 },
  xfader = 0.5, musicVol = 0.85, masterVol = 1.0,
  micEnabled = false,
  micDeviceId = "",            // "" = default / system input
  micMusicMode = false,         // false=talk mode (echo cancel + noise suppress), true=line-in/music mode (clean stereo, no DSP)
  micGain = 0.85,               // 0..1 — mic input level (independent from music vol)
  micFxSend = false,            // true = mic goes through FX bus, false = direct to master (clean talk)
  cueA = false, cueB = false,   // headphone-cue: when true, bypass xfader for that deck
  scratchA = false, scratchB = false,  // scratch monitor: force the deck audible at the configured boost while user drags the platter
  scratchBoost = 1.0,           // 0..1 — how loud the scratch rub is mixed (master-bar knob)
  fxSendA = true, fxSendB = true, // per-deck FX route — when false, that deck plays clean direct to master
  fxType = "off",               // "off" | "echo" | "filter" | "flanger"
  fxAmount = 0.0,               // 0..1
}) {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);         // GainNode — final stage before destination
  const preFxRef = useRef(null);          // GainNode — sum of all channels BEFORE FX
  const fxWetRef = useRef(null);          // GainNode — wet signal in
  const fxDryRef = useRef(null);          // GainNode — dry passthrough
  const fxNodesRef = useRef({});          // { delay, feedback, filter, flangerDelay, flangerLfo, flangerLfoGain }
  const broadcastRef = useRef(null);      // MediaStreamAudioDestinationNode
  const micRef = useRef(null);
  const deckA = useRef(null);
  const deckB = useRef(null);

  const isBooted = () => !!ctxRef.current;

  const ensureBoot = () => {
    if (ctxRef.current) return ctxRef.current;
    const Ctx = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
    if (!Ctx) return null;
    const ctx = new Ctx();
    const preFx = ctx.createGain();   // sum of channels + mic BEFORE FX
    const fxDry = ctx.createGain(); fxDry.gain.value = 1.0;
    const fxWet = ctx.createGain(); fxWet.gain.value = 0.0;
    const master = ctx.createGain();
    const broadcast = ctx.createMediaStreamDestination();

    // FX nodes (idle by default — wet gain is 0 so they contribute nothing)
    const delay = ctx.createDelay(2.0); delay.delayTime.value = 0.375; // 8th-note at 120bpm
    const feedback = ctx.createGain(); feedback.gain.value = 0.0;
    const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 20000; filter.Q.value = 1;
    const flangerDelay = ctx.createDelay(0.02); flangerDelay.delayTime.value = 0.005;
    const flangerLfo = ctx.createOscillator(); flangerLfo.frequency.value = 0.5;
    const flangerLfoGain = ctx.createGain(); flangerLfoGain.gain.value = 0.003;
    flangerLfo.connect(flangerLfoGain).connect(flangerDelay.delayTime);
    try { flangerLfo.start(); } catch {}

    // Signal flow: preFx → fxDry → master → destination
    //              preFx → (fx chain) → fxWet → master
    preFx.connect(fxDry).connect(master);
    // Echo path: preFx → delay → feedback → delay (feedback loop) → fxWet
    preFx.connect(delay);
    delay.connect(feedback).connect(delay);
    delay.connect(fxWet);
    // Filter path: preFx → filter → fxWet (we'll re-route fxWet connections per fxType change below)
    // (Connections to fxWet are switched via .disconnect()/.connect() in the fxType effect.)
    fxWet.connect(master);
    master.connect(ctx.destination);
    master.connect(broadcast);

    ctxRef.current = ctx;
    masterRef.current = master;
    preFxRef.current = preFx;
    fxDryRef.current = fxDry;
    fxWetRef.current = fxWet;
    fxNodesRef.current = { delay, feedback, filter, flangerDelay };
    broadcastRef.current = broadcast;
    return ctx;
  };

  // Build / refresh a deck's chain for the current <audio> element.
  const attachDeck = (side, ref, trackId) => {
    const ctx = ensureBoot();
    if (!ctx) return;
    const el = ref?.current;
    const slot = side === "A" ? deckA : deckB;
    if (!el) return;
    if (slot.current?.el === el) return; // already attached
    // Tear down prior nodes for this side
    if (slot.current) {
      try {
        slot.current.src?.disconnect?.();
        slot.current.low?.disconnect?.();
        slot.current.mid?.disconnect?.();
        slot.current.hi?.disconnect?.();
        slot.current.chan?.disconnect?.();
        slot.current.fxSend?.disconnect?.();
        slot.current.fxBypass?.disconnect?.();
      } catch {}
    }
    try {
      const src = ctx.createMediaElementSource(el);
      const low = ctx.createBiquadFilter(); low.type = "lowshelf"; low.frequency.value = 120;
      const mid = ctx.createBiquadFilter(); mid.type = "peaking";  mid.frequency.value = 1000; mid.Q.value = 1.0;
      const hi  = ctx.createBiquadFilter(); hi.type  = "highshelf"; hi.frequency.value = 8000;
      const chan = ctx.createGain(); chan.gain.value = 0;
      // Per-deck FX send: chan splits into two paths.
      //   • fxSend → preFx (FX bus → fxWet → master)
      //   • fxBypass → master (clean, dry)
      // The host toggles which deck goes through FX by flipping these gains.
      // Defaults: fxSend=1, fxBypass=0 (legacy "all decks share FX" behavior),
      // updated by the per-deck FX-send useEffect below.
      const fxSend = ctx.createGain(); fxSend.gain.value = 1.0;
      const fxBypass = ctx.createGain(); fxBypass.gain.value = 0.0;
      src.connect(low).connect(mid).connect(hi).connect(chan);
      chan.connect(fxSend).connect(preFxRef.current);
      chan.connect(fxBypass).connect(masterRef.current);
      slot.current = { el, src, low, mid, hi, chan, fxSend, fxBypass };
    } catch (e) {
      // Most common reason: element already has a source. Safe to ignore
      // since we only end up here when the audio element hasn't changed.
      // eslint-disable-next-line no-console
      console.warn("[DjMixerEngine] attach failed", e);
    }
  };

  // ATTACH: whenever the loaded track for a deck changes OR we first mount,
  // try to attach. `trackAId`/`trackBId` change together with the `<audio>
  // key` in the parent so the element will be fresh.
  useEffect(() => {
    attachDeck("A", audioARef, trackAId);
    attachDeck("B", audioBRef, trackBId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackAId, trackBId]);

  // Tune EQ knobs on changes
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    if (deckA.current) {
      deckA.current.low.gain.setTargetAtTime(knobToDb(eqA.low), now, 0.015);
      deckA.current.mid.gain.setTargetAtTime(knobToDb(eqA.mid), now, 0.015);
      deckA.current.hi.gain.setTargetAtTime(knobToDb(eqA.hi),  now, 0.015);
    }
    if (deckB.current) {
      deckB.current.low.gain.setTargetAtTime(knobToDb(eqB.low), now, 0.015);
      deckB.current.mid.gain.setTargetAtTime(knobToDb(eqB.mid), now, 0.015);
      deckB.current.hi.gain.setTargetAtTime(knobToDb(eqB.hi),  now, 0.015);
    }
  }, [eqA.low, eqA.mid, eqA.hi, eqB.low, eqB.mid, eqB.hi]);

  // Channel + master gain — equal-power crossfade + GAIN knob + musicVol.
  // CUE bypass: when a deck is cued (headphone preview) the channel plays at
  // unity regardless of crossfader position so the DJ can audition.
  // SCRATCH bypass: when a deck is being scratched, the channel gain is
  // MAX(current xfader gain, scratchBoost) — i.e. the rub is never QUIETER
  // than the deck normally would be. The earlier impl replaced the xfader
  // gain with `scratchBoost` (default 0.45) which silently DUCKED a loud
  // deck during a scratch — making the rub inaudible. We also use a much
  // shorter smoothing time constant (5ms vs 20ms) while a deck is being
  // scratched so rapid wrist motion doesn't pump the gain envelope.
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const xA = Math.cos((xfader * Math.PI) / 2);
    const xB = Math.sin((xfader * Math.PI) / 2);
    const minBoost = Math.max(0.05, scratchBoost);
    const gA = cueA ? 1.0 : (scratchA ? Math.max(xA, minBoost) : xA);
    const gB = cueB ? 1.0 : (scratchB ? Math.max(xB, minBoost) : xB);
    const gainKnobA = eqA.gain * 2;
    const gainKnobB = eqB.gain * 2;
    const now = ctx.currentTime;
    // 5ms smoothing while scratching (responsive to wrist motion), 20ms
    // otherwise (click-free crossfader moves).
    const tcA = scratchA ? 0.005 : 0.02;
    const tcB = scratchB ? 0.005 : 0.02;
    if (deckA.current) deckA.current.chan.gain.setTargetAtTime(gA * gainKnobA * musicVol, now, tcA);
    if (deckB.current) deckB.current.chan.gain.setTargetAtTime(gB * gainKnobB * musicVol, now, tcB);
    if (masterRef.current) masterRef.current.gain.setTargetAtTime(masterVol, now, 0.02);
  }, [xfader, musicVol, masterVol, eqA.gain, eqB.gain, trackAId, trackBId, cueA, cueB, scratchA, scratchB, scratchBoost]);

  // Per-deck FX-send routing — flips the dry / wet split per deck.
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const setSend = (slot, on) => {
      if (!slot?.current?.fxSend || !slot?.current?.fxBypass) return;
      slot.current.fxSend.gain.setTargetAtTime(on ? 1.0 : 0.0, now, 0.02);
      slot.current.fxBypass.gain.setTargetAtTime(on ? 0.0 : 1.0, now, 0.02);
    };
    setSend(deckA, !!fxSendA);
    setSend(deckB, !!fxSendB);
  }, [fxSendA, fxSendB, trackAId, trackBId]);

  // FX routing — reshape the wet-path nodes when type or amount changes.
  // Amount (0..1) is the fader from "dry only" (0) to "max effect".
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const preFx = preFxRef.current;
    const wet = fxWetRef.current;
    const dry = fxDryRef.current;
    const { delay, feedback, filter, flangerDelay } = fxNodesRef.current;
    if (!preFx || !wet || !dry) return;

    const now = ctx.currentTime;
    // Disconnect optional routes — safe to call repeatedly
    try { preFx.disconnect(filter); } catch {}
    try { filter.disconnect(wet); } catch {}
    try { preFx.disconnect(flangerDelay); } catch {}
    try { flangerDelay.disconnect(wet); } catch {}
    // NOTE: the echo path (preFx → delay → feedback → delay → wet) was wired
    //       at boot and we just gate it via feedback gain + wet gain.

    // Base: wet gain scales with amount (sqrt taper for perceptual balance).
    // Dry stays at unity; for true wet-only at amount=1, we'd duck dry, but
    // DJ FX usually keep the dry signal present.
    wet.gain.setTargetAtTime(Math.sqrt(fxAmount), now, 0.03);
    dry.gain.setTargetAtTime(1.0, now, 0.03);

    if (fxType === "echo") {
      // Wire feedback 0..0.7 so it doesn't self-destruct at amount=1
      feedback.gain.setTargetAtTime(fxAmount * 0.6, now, 0.05);
    } else {
      feedback.gain.setTargetAtTime(0, now, 0.02);
    }

    if (fxType === "filter") {
      // Sweep: amount<0.5 => lowpass closing (20k→400Hz), amount>0.5 => highpass opening (40Hz→6k)
      if (fxAmount < 0.5) {
        filter.type = "lowpass";
        const cutoff = 20000 * Math.pow(1 - fxAmount * 2, 2) + 400;
        filter.frequency.setTargetAtTime(cutoff, now, 0.03);
      } else {
        filter.type = "highpass";
        const cutoff = 40 * Math.pow(1 + (fxAmount - 0.5) * 12, 2);
        filter.frequency.setTargetAtTime(cutoff, now, 0.03);
      }
      preFx.connect(filter); filter.connect(wet);
      // No echo or flanger
      wet.gain.setTargetAtTime(fxAmount > 0.02 ? 1.0 : 0.0, now, 0.03); // filter is fully wet
    }

    if (fxType === "flanger") {
      // Short delay feeds the wet bus
      flangerDelay.delayTime.setTargetAtTime(0.003 + fxAmount * 0.008, now, 0.03);
      preFx.connect(flangerDelay); flangerDelay.connect(wet);
    }

    if (fxType === "off") {
      wet.gain.setTargetAtTime(0, now, 0.03);
    }
  }, [fxType, fxAmount]);

  // Microphone — enabled when `micEnabled` is true. Re-subscribes whenever
  // the host picks a different input device so external mixers / audio
  // interfaces / bluetooth mics can be routed in.
  // `micMusicMode` flips the constraints between voice (echo-cancel +
  // noise-suppression on, mono) and line-in/music (everything off, stereo)
  // so a DJ can plug a phone / second laptop / interface into the mic jack
  // and use it as a music source without the browser scrubbing the bass
  // out of the signal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ctx = ensureBoot();
      if (!ctx) return;
      // Tear down any existing mic before we either disable or switch device
      const tearDown = () => {
        const m = micRef.current;
        if (m) {
          try { m.node.disconnect(); m.gain.disconnect(); m.fxSend?.disconnect(); m.fxBypass?.disconnect(); } catch {}
          try { m.stream.getTracks().forEach((t) => t.stop()); } catch {}
          micRef.current = null;
        }
      };
      if (!micEnabled) { tearDown(); return; }
      // If already attached with the same device + mode, keep as-is
      if (micRef.current?.node && micRef.current?.deviceId === (micDeviceId || "") && micRef.current?.musicMode === !!micMusicMode) return;
      tearDown();
      try {
        const audioConstraints = micMusicMode
          ? {
              // Music / line-in mode — capture as clean as the browser
              // will give us. Bass + presence stay intact.
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
              channelCount: 2,
              ...(micDeviceId ? { deviceId: { exact: micDeviceId } } : {}),
            }
          : {
              // Talk mode — typical broadcast mic processing
              echoCancellation: true,
              noiseSuppression: true,
              ...(micDeviceId ? { deviceId: { exact: micDeviceId } } : {}),
            };
        const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints, video: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        const node = ctx.createMediaStreamSource(stream);
        const gain = ctx.createGain(); gain.gain.value = Math.max(0, Math.min(2, micGain * 2));
        // Same dry/wet split as the decks so the mic can independently
        // route through FX (great for music-in) or stay clean (talk).
        const fxSend = ctx.createGain(); fxSend.gain.value = micFxSend ? 1.0 : 0.0;
        const fxBypass = ctx.createGain(); fxBypass.gain.value = micFxSend ? 0.0 : 1.0;
        node.connect(gain);
        gain.connect(fxSend).connect(preFxRef.current);
        gain.connect(fxBypass).connect(masterRef.current);
        micRef.current = { stream, node, gain, fxSend, fxBypass, deviceId: micDeviceId || "", musicMode: !!micMusicMode };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[DjMixerEngine] mic denied", e);
      }
    })();
    return () => { cancelled = true; };
  }, [micEnabled, micDeviceId, micMusicMode]);

  // Live-tune mic gain + FX-send without rebuilding the mic graph.
  useEffect(() => {
    const ctx = ctxRef.current;
    const m = micRef.current;
    if (!ctx || !m) return;
    const now = ctx.currentTime;
    m.gain.gain.setTargetAtTime(Math.max(0, Math.min(2, micGain * 2)), now, 0.02);
    m.fxSend?.gain?.setTargetAtTime(micFxSend ? 1.0 : 0.0, now, 0.02);
    m.fxBypass?.gain?.setTargetAtTime(micFxSend ? 0.0 : 1.0, now, 0.02);
  }, [micGain, micFxSend]);

  // Resume context on any user-visible interaction (click anywhere)
  useEffect(() => {
    const resume = () => {
      const ctx = ctxRef.current;
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    window.addEventListener("pointerdown", resume, { once: false });
    return () => window.removeEventListener("pointerdown", resume);
  }, []);

  // Teardown on unmount
  useEffect(() => {
    return () => {
      try { micRef.current?.stream?.getTracks()?.forEach((t) => t.stop()); } catch {}
      try { ctxRef.current?.close?.(); } catch {}
      ctxRef.current = null; masterRef.current = null; broadcastRef.current = null; micRef.current = null; deckA.current = null; deckB.current = null;
    };
  }, []);

  // Track-like sources (mic + callers) → preFx bus so they go through FX + master.
  // { key: { node, gain } }
  const externalRef = useRef({});

  // Attach a named MediaStream (caller / bot) to the mix. Idempotent by key.
  const attachStream = useCallback((key, stream, { gainValue = 1.0 } = {}) => {
    const ctx = ensureBoot(); if (!ctx) return false;
    const existing = externalRef.current[key];
    if (existing) detachStream(key);
    try {
      const node = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain(); gain.gain.value = gainValue;
      node.connect(gain).connect(preFxRef.current);
      externalRef.current[key] = { node, gain, stream };
      return true;
    } catch { return false; }
  }, []);

  // Detach by key. Also stops the stream's tracks so browser mic indicator clears.
  const detachStream = useCallback((key) => {
    const e = externalRef.current[key];
    if (!e) return;
    try { e.node.disconnect(); e.gain.disconnect(); } catch {}
    try { e.stream?.getTracks()?.forEach?.((t) => t.stop()); } catch {}
    delete externalRef.current[key];
  }, []);

  return {
    isBooted,
    getBroadcastStream: () => broadcastRef.current?.stream || null,
    getContextState: () => ctxRef.current?.state || "closed",
    attachStream,
    detachStream,
  };
}
